// ジョブコミ独自: type="job_komi_min_wage"の時給フィールド用バリデーション。
// data-job-komi-facility-key / data-job-komi-date-key で指定された施設選択フィールド・
// 基準日(勤務日)フィールドから、選択施設の都道府県における基準日時点で有効な最低賃金を判定する。
// form-validator.jsの汎用min_wageルール(フォーム上の実在するprefecture_idセレクトを直接参照する設計)
// では、施設選択経由の間接的な都道府県特定に対応できないため、別ルールとして分離している。

// toggle-form.jsのtoggle-trigger-keyと同様、同じフォーム内の別フィールドを
// (nameのkey部分を置換して)キー名で参照する。form-validator.jsのfindFieldByKeyと同じ実装。
const findFieldByKey = (el, key) => {
  const form = $(el).closest('form')
  const name = $(el).attr('name')
  const targetName = name.replace(/\[(?!.*\[).*/, '[' + key + ']')
  return form.find('input[name="' + targetName + '"],select[name="' + targetName + '"]')
}

// 施設選択フィールドの値は、single_selectなら選択肢idそのもの("5")、
// multiple_hierarchy_select(hierarchy-checkbox.js)ならJSON配列文字列('["12140"]')
// と、選択肢の種別によって形が異なる。どちらの形でも先頭の選択肢idを取り出す。
const extractFacilityId = (rawVal) => {
  let parsed
  try {
    parsed = JSON.parse(rawVal)
  } catch (e) {
    parsed = rawVal
  }
  const id = Array.isArray(parsed) ? parsed[0] : parsed
  return id === undefined || id === null || id === '' ? null : String(id)
}

// 基準日ごとの最低賃金データをキャッシュ
let jobKomiMinWagesDataMapByDate = {}
// 基準日ごとの進行中APIフェッチのPromiseを保持
let jobKomiMinWagesDataFetchPromises = {}

// 基準日ごとに最低賃金データをAPIから取得し、キャッシュする関数
const fetchJobKomiMinWagesData = (date) => {
  if (jobKomiMinWagesDataMapByDate[date]) {
    return Promise.resolve(jobKomiMinWagesDataMapByDate[date])
  }
  if (jobKomiMinWagesDataFetchPromises[date]) {
    return jobKomiMinWagesDataFetchPromises[date]
  }

  jobKomiMinWagesDataFetchPromises[date] = fetch(`/api/v1/m_min_wages?date=${date}`)
    .then(response => response.json())
    .then(apiResponse => {
      const minWagesArray = apiResponse.m_min_wages
      const map = {}
      if (minWagesArray.length) {
        minWagesArray.forEach(item => {
          map[item.prefecture_id] = item.min_wage
        })
      }
      jobKomiMinWagesDataMapByDate[date] = map
      return map
    })
    .catch(error => {
      console.error('job_komi_min_wage API fetch error:', error)
      return null
    })
    .finally(() => {
      delete jobKomiMinWagesDataFetchPromises[date]
    })
  return jobKomiMinWagesDataFetchPromises[date]
}

// form-validator.jsの'job_komi_min_wage'ルールから呼び出されるバリデーション本体
export const validJobKomiMinWage = (value, el) => {
  if ($(el).prop('disabled') || $(el).prop('readonly')) {
    // readonly(編集不可)のフィールドは本人が変更できないため、最低賃金の改定で
    // 新たに下回ってもエラー扱いにしない(そのまま送信ブロックされ続けるのを防ぐ)
    return true
  }

  if (!value) {
    return true
  }
  const numericValue = parseInt(value)
  if (isNaN(numericValue)) {
    return true
  }

  const facilityKey = $(el).data('job-komi-facility-key')
  const dateKey = $(el).data('job-komi-date-key')
  if (!facilityKey || !dateKey) {
    return true
  }

  const facilityRawVal = findFieldByKey(el, facilityKey).val()
  const dateVal = findFieldByKey(el, dateKey).val()
  if (!facilityRawVal || !dateVal) {
    // 施設・基準日が未選択の間は判定不可(presenceルールで別途必須チェックされる)
    return true
  }
  const facilityId = extractFacilityId(facilityRawVal)
  if (!facilityId) {
    return true
  }

  const facilitiesMap = $(el).data('job-komi-facilities') || {}
  const facility = facilitiesMap[facilityId]
  if (!facility) {
    // SiteChoiceとJobKomiFacilityマスタが未同期の施設は判定をスキップする
    return true
  }

  const dateOnly = dateVal.slice(0, 10) // datetime-local等の値から日付部分のみ取り出す
  const cached = jobKomiMinWagesDataMapByDate[dateOnly]
  if (!cached) {
    // 未取得の場合は取得をキックし、取得完了後に再検証する(今回の判定はスキップ扱い)
    fetchJobKomiMinWagesData(dateOnly).then((map) => {
      // 取得成功時のみ再検証する(失敗時は再試行を無限ループさせない)
      if (map) {
        $(el).trigger('change')
      }
    })
    return true
  }

  const minWageForFacility = cached[facility.prefecture_id]
  if (!minWageForFacility) {
    return true
  }

  $(el).data('job-komi-prefecture-name', facility.prefecture_name)
  $(el).data('job-komi-min-wage', minWageForFacility)
  if (numericValue < minWageForFacility) {
    return false
  }

  return true
}

// フォーム内の施設・基準日(勤務日)フィールドが変更された時、対応する時給フィールドの
// 最低賃金バリデーションを再実行する。form-validator.jsのonChangeFieldから呼び出される。
export const retriggerJobKomiMinWageOnChange = (form, changedField) => {
  form.find('input[data-rules*="job_komi_min_wage"]').each((i, wageEl) => {
    const $wageEl = $(wageEl)
    if (!$wageEl.val().length) {
      return
    }
    const facilityKey = $wageEl.data('job-komi-facility-key')
    const dateKey = $wageEl.data('job-komi-date-key')
    if (!facilityKey || !dateKey) {
      return
    }
    const facilityEl = findFieldByKey(wageEl, facilityKey)
    const dateEl = findFieldByKey(wageEl, dateKey)
    if (changedField.is(facilityEl) || changedField.is(dateEl)) {
      $wageEl.trigger('change')
    }
  })
}

// ページ読み込み時、施設・基準日が入力済みのjob_komi_min_wageフィールドがあれば
// 予め最低賃金データを取得しておく。form-validator.jsの初期化処理から呼び出される。
export const prefetchJobKomiMinWage = () => {
  $('.js-form-validation').find('input[data-rules*="job_komi_min_wage"]').each((i, el) => {
    const dateKey = $(el).data('job-komi-date-key')
    if (!dateKey) {
      return
    }
    const dateVal = findFieldByKey(el, dateKey).val()
    if (dateVal) {
      fetchJobKomiMinWagesData(dateVal.slice(0, 10))
    }
  })
}
